import { useQuery } from '@tanstack/react-query';
import track, { useTracking } from 'react-tracking';
import fetchMarketMovers, {
  getDefault as getDefaultMarketMovers,
  MARKET_MOVER_QUERY_KEY,
} from '@/pages/Market/query/fetchMarketMovers';
import {
  ScrollPane,
  SnowflakeColumnHeader,
} from '@/pages/Stocks/components/StockTable/styled';
import {
  Head,
  NameColumnHeaderStyled,
  Table,
  Wrapper,
  SeeMoreButton,
} from './styled';
import { Tab, TabList } from '@simplywallst/ui-core';
import type { TabOnClickEvent } from '@simplywallst/ui-core';
import { ROUTE_STOCKS_MARKET } from '@/constants/routes';

import TableRow from './Row';
import type { UIEvent } from 'react';
import { useCallback, useState } from 'react';
import useReduxStateMarket, {
  MarketPageType,
} from '@/pages/Market/hooks/useReduxStateMarkets';
import useIndustryGetter from '@/pages/Market/hooks/useIndustryGetter';
import RowSkeleton from './RowSkeleton';
import { NoResults } from '@/pages/Market/components/Sectors/styled';
import { HeaderToolTip } from '@/pages/Stocks/components/StockTable/StockTable';

enum MarketMoversTab {
  top = 'Top Gainers',
  worst = 'Top Losers',
}

type MarketMoversTabType = keyof typeof MarketMoversTab;

const MarketTable = () => {
  const { market, marketPageType } = useReduxStateMarket();
  const [{ id }, enabled] = useIndustryGetter();
  const {
    data: marketMovers = getDefaultMarketMovers(),
    isFetching,
    isFetched,
  } = useQuery({
    queryKey: [MARKET_MOVER_QUERY_KEY, market, id],
    queryFn: fetchMarketMovers,
    staleTime: Infinity,
    enabled: marketPageType === MarketPageType.MARKET ? true : enabled,
    refetchOnMount: false,
  });
  const { trackEvent } = useTracking();
  const [activeTab, setActiveTab] = useState<MarketMoversTabType>('top');
  const handleTabSelect = useCallback(
    (event: TabOnClickEvent<MarketMoversTabType>) => {
      const value = event.target.value;
      setActiveTab(value);
      trackEvent({
        action: 'click',
        subject: 'market-movers-tab',
        tab: MarketMoversTab[value],
      });
    },
    []
  );
  const [isSticky, setIsSticky] = useState(false);
  const handlePaneScroll = useCallback(
    (e: UIEvent) => {
      const { currentTarget } = e;
      if (currentTarget.scrollLeft >= 55 && !isSticky) {
        setIsSticky(true);
      } else if (currentTarget.scrollLeft < 55 && isSticky) {
        setIsSticky(false);
      }
    },
    [isSticky]
  );

  const isMarketPage = marketPageType === MarketPageType.MARKET;

  const currentMarketMovers = marketMovers[activeTab] || [];

  return (
    <Wrapper>
      <TabList templateColumns="max-content max-content">
        <Tab
          key="top"
          data-cy-id="market-movers-gainers-tab"
          selected={activeTab === 'top'}
          onClick={handleTabSelect}
          value="top"
          disabled={isFetching}
        >
          {MarketMoversTab.top}
        </Tab>
        <Tab
          key="worst"
          data-cy-id="market-movers-losers-tab"
          selected={activeTab === 'worst'}
          onClick={handleTabSelect}
          value="worst"
          disabled={isFetching}
        >
          {MarketMoversTab.worst}
        </Tab>
      </TabList>
      <ScrollPane onScroll={handlePaneScroll}>
        <Table disabled={isFetching}>
          <Head>
            <tr>
              <SnowflakeColumnHeader></SnowflakeColumnHeader>
              <NameColumnHeaderStyled isSticky={isSticky}>
                Company
              </NameColumnHeaderStyled>
              <th>Last Price</th>
              <th>7D</th>
              <th>1Y</th>
              <th>
                Valuation
                <HeaderToolTip>
                  Valuation ratios are used to determine the relative value of a
                  company compared to similar companies when applicable. <br />
                  <br />
                  Key ratios:
                  <br />
                  <strong>PE</strong> – Price to Earnings
                  <br />
                  <strong>PS</strong> – Price to Sales
                  <br />
                  <strong>PB</strong> – Price to Book
                </HeaderToolTip>
              </th>
              {isMarketPage && <th>Sector</th>}
            </tr>
          </Head>
          <tbody>
            {!isFetching ? (
              currentMarketMovers.map((company) => (
                <TableRow
                  key={company.companyId}
                  companyId={company.companyId}
                  tickerSymbol={company.tickerSymbol}
                  sectorSlug={company.sectorSlug}
                  return1YrAbs={company.return1YrAbs}
                  return1YrAbsFormatted={company.return1YrAbsFormatted}
                  return7D={company.return7D}
                  return7DFormatted={company.return7DFormatted}
                  sector={company.sector}
                  sectorId={company.sectorId}
                  shortDescription={company.shortDescription}
                  shortName={company.shortName}
                  snowflakePoints={company.snowflakePoints}
                  uniqueSymbol={company.uniqueSymbol}
                  bestValuationRatio={company.bestValuationRatio}
                  canonicalUrl={company.canonicalUrl}
                  pbFormatted={company.pbFormatted}
                  psFormatted={company.psFormatted}
                  peFormatted={company.peFormatted}
                  marketCapMovement7DFormatted={
                    company.marketCapMovement7DFormatted
                  }
                  isSticky={isSticky}
                  market={market}
                  isMarketPage={isMarketPage}
                  lastSharePrice={company.lastSharePrice}
                  lastSharePriceFormatted={company.lastSharePriceFormatted}
                />
              ))
            ) : (
              <RowSkeleton row={5} isMarketPage={isMarketPage} />
            )}
            {isFetched && currentMarketMovers.length === 0 && (
              <tr>
                <NoResults colSpan={6} data-cy-id="market-movers-no-results">
                  No data available
                </NoResults>
              </tr>
            )}
          </tbody>
        </Table>
      </ScrollPane>
      {isMarketPage && (
        <SeeMoreButton
          onClick={() =>
            trackEvent({
              action: 'click',
              subject: 'market-movers-see-more',
            })
          }
          display="block"
          styleType="loudLarge"
          to={{
            type: ROUTE_STOCKS_MARKET,
            payload: {
              market,
              sortBy: activeTab === 'top' ? 'top-gainers' : 'biggest-losers',
            },
          }}
        >
          See more
        </SeeMoreButton>
      )}
    </Wrapper>
  );
};

export default track(
  {
    type: 'track',
    subject: 'market-mover',
  },
  { dispatchOnMount: true }
)(MarketTable) as typeof MarketTable;
